import {
  Component,
  computed,
  inject,
  input,
  output,
  model,
  TemplateRef,
  viewChild,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateDirective } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AnalystRatingComponent,
  AnalystRatingTooltipComponent,
  BuySellButtonsComponent,
  Breakpoint,
  BreakpointState,
  DatePipe,
  DetailedQuoteMessageComponent,
  DetailedQuoteMoreDetailsComponent,
  DetailedQuoteSummaryComponent,
  DetailedSymbolHeaderComponent,
  EmptyComponent,
  EmptyHeaderComponent,
  HrefDirective,
  IconComponent,
  IfFeatureDirective,
  IfNoFeatureDirective,
  LoadingIndicatorPanelComponent,
  LocalStorageService,
  MediaQueryService,
  NotificationComponent,
  QuoteChartComponent,
  QuoteLinkComponent,
  StarRatingComponent,
  TabComponent,
  TabsComponent,
  UtilityService,
  ContainerSizeDirective,
  ContainerSize,
  DetailedQuote,
  QuoteChartSize,
  QuoteIssueType,
  QuoteTemplate,
  QuoteTimeRange,
  QuoteTimeSeries,
  QuoteChartSettings,
  QuoteFrequencyParams,
  QuoteComponent,
  ChartTypeSelector,
  FrequencySelector,
  QuoteConstants,
  TimeRangeSelector,
  DetailedQuoteTab,
  MarketTypeHelper,
  DetailedLoadingIndicatorComponent
} from '@tdwb/core';

@Component({
  selector: 'td-wb-markets-detailed-quote',
  templateUrl: 'detailed-quote.component.html',
  styleUrls: ['detailed-quote.component.scss'],
  imports: [
    NgTemplateOutlet,
    ContainerSizeDirective,
    TranslateDirective,
    LoadingIndicatorPanelComponent,
    DetailedSymbolHeaderComponent,
    NotificationComponent,
    IfNoFeatureDirective,
    IfFeatureDirective,
    EmptyComponent,
    EmptyHeaderComponent,
    DetailedQuoteMessageComponent,
    QuoteChartComponent,
    QuoteLinkComponent,
    TabsComponent,
    TabComponent,
    DetailedQuoteSummaryComponent,
    DetailedQuoteMoreDetailsComponent,
    StarRatingComponent,
    AnalystRatingTooltipComponent,
    AnalystRatingComponent,
    IconComponent,
    BuySellButtonsComponent,
    DatePipe,
    DetailedLoadingIndicatorComponent,
    HrefDirective
  ]
})
export class DetailedQuoteComponent extends QuoteComponent {
  readonly viewSize = input<QuoteChartSize | undefined>(undefined);
  readonly selectedTab = model<DetailedQuoteTab | undefined>(undefined);
  readonly hideAddToWatchlistButton = input<boolean | undefined>(undefined);
  readonly hideInterlistExchangeForOTC = input<boolean>(false);
  readonly showReinvestToggle = input<boolean>(false);
  readonly reinvestSelection = input<boolean>(false);
  readonly reinvestToggleId = input<string>('');

  readonly reinvestChange = output<boolean>();
  private readonly templateRefIssueType = viewChild<TemplateRef<unknown>>('issueType');

  protected readonly analystRatingEnabled: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  readonly DetailedQuoteTab: typeof DetailedQuoteTab = DetailedQuoteTab;

  private readonly customizedChartEnabled: boolean;
  private readonly change: Subject<void> = new Subject<void>();
  private readonly breakpointState = signal<BreakpointState | undefined>(undefined);
  private readonly containerSize = signal<ContainerSize | null>(null);

  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly localStorageService = inject(LocalStorageService);

  constructor() {
    super();
    this.analystRatingEnabled = this.securityService.hasPermission('HOME_PAGE_ANALYST_RATINGS');
    this.customizedChartEnabled = this.securityService.hasPermission('QUOTE_DETAIL_CUSTOMIZED_CHART');
    if (this.customizedChartEnabled === true) {
      this.displayTimeSeries.customizedChartEnabled = true;
      this.displayTimeSeries.types = QuoteConstants.CHART_TYPE_SELECTOR;
    }
    this.mediaQueryService.observe(Breakpoint.SM)
      .pipe(takeUntilDestroyed())
      .subscribe((breakpointState: BreakpointState): void => {
        this.breakpointState.set(breakpointState);
      });
  }

  containerSizeChange(containerSize: ContainerSize): void {
    this.containerSize.set(containerSize);
  }

  protected readonly isLoading = computed(() => this.containerSize() === null || this.loading());
  protected readonly isSmallScreen = computed(() => this.breakpointState()?.matches ?? false);
  protected readonly hasModPermission = computed(() => this.securityService.hasPermission('MOD'));

  /**
   * Show inter-listed exchanges
   */
  showInterlistedExchangeQuote(): void {
    const tickerSymbol = this.quote().interlistedExchanges[0].symbol;
    const country = this.quote().interlistedExchanges[0].country;

    this.interlistedExchangeSymbol = UtilityService.deepClone(this.symbol());
    this.interlistedExchangeSymbol.tickerSymbol = tickerSymbol;
    this.interlistedExchangeSymbol.market = MarketTypeHelper.fromString(country);
    this.refreshQuote();
  }

  isMutualFund(): boolean {
    return this.quote().issueType === QuoteIssueType.MF;
  }

  /**
   * return true if Morning star rating displayed for current security
   */
  showStarRating(): boolean {
    const quote = this.quote();
    return quote.issueType === QuoteIssueType.MF
        || quote.issueType === QuoteIssueType.ETF;
  }

  showAnalystRating(): boolean {
    const quote = this.quote();
    if (quote !== undefined && quote.consensus !== undefined && typeof quote.consensus === 'object') {
      return this.analystRatingEnabled === true
        && quote.issueType === QuoteIssueType.STOCK
        && quote.consensus.overallConsensus !== undefined;
    }
    return this.analystRatingEnabled === true
      && quote.issueType === QuoteIssueType.STOCK
      && quote.consensus !== undefined;
  }

  showWarningMessage(): boolean {
    const quote = this.quote();
    return quote.issueType !== QuoteIssueType.MF
        && quote.issueType !== QuoteIssueType.OPTION;
  }

  /**
   * return true if buy sell enabled for current security
   */
  showBuySell(): boolean {
    return this.inactivateBuySell() !== true
        && this.tradeInfo !== undefined
        && this.quote().issueType !== QuoteIssueType.INDEX;
  }

  /**
   * Handle tab change
   */
  switchTab(tabId: DetailedQuoteTab): void {
    this.selectedTab.set(tabId);
    let storedObject = this.localStorageService.getItem(QuoteConstants.LOCAL_STORAGE_KEY);
    storedObject ??= {};

    const key = this.getLocalStorageQuoteType(this.quote().issueType);
    storedObject[key] = this.selectedTab();
    this.localStorageService.setItem(QuoteConstants.LOCAL_STORAGE_KEY, storedObject);
  }

  onFrequencyChange(): void {
    this.saveChartSettings();
    this.refreshQuote();
  }

  // Chart type dropdown change callback.
  // Stores selection for later and , since we are only changing chart type and do not need new data,
  // uses internal seriesData and immediately invokes requestTimeSeriesSuccess sending ohlcSeries as if was a response
  onTypeChange(): void {
    this.saveChartSettings();
    const timeRangeType = this.displayTimeSeries.selectTimeRange.type;
    if (timeRangeType === QuoteTimeRange.DAILY) {
      this.requestTimeSeriesSuccess({ ohlcSeries: this.seriesData }, undefined);
    } else {
      const chartSeries = this.displayTimeSeries.selectTimeRange.chartSeries;
      const updateSeries = this.dailyTimeSeriesFrequency(chartSeries);
      this.requestTimeSeriesSuccess({ ohlcSeries: this.seriesData }, updateSeries);
    }
  }

  /**
   * Handles on tab change event
   * Creates new chartConfig object so that child components
   * can detect changes in chartConfig
   *
   * Refresh chartConfig based on selected tab
   */
  onTabChange(tabId: number): void {
    if (this.customizedChartEnabled) {
      this.displayTimeSeries.value = tabId;
      this.onTimeRangeChange();
    } else {
      super.onTabChange(tabId);
    }
  }

  /**
   * Handles time range change
   */
  onTimeRangeChange(): void {
    const selectTimeRange = this.displayTimeSeries.selectTimeRange;
    this.assignmentFrequencies(selectTimeRange, undefined);
    this.saveChartSettings();
    this.refreshQuote();
  }

  /**
   * Returns chart size based on the current window size
   */
  getChartSize(): QuoteChartSize {
    const size = this.containerSize();
    if ((size !== undefined && size === ContainerSize.SM) || this.viewSize() === 'compact') {
      return 'compact';
    }
    return 'full';
  }

  /**
   * Refresh quote. To be used externally for now
   */
  refresh(): void {
    this.refreshQuote();
  }

  /**
   * Gets template based on issue type
   */
  protected getIssueTypeTemplate(): TemplateRef<unknown> | undefined {
    return this.templateRefIssueType();
  }

  /**
   * Called on quote retrieval success
   *
   * Updates template and quote object
   * Emits new quote object
   *
   */
  protected requestQuoteSuccess(quote: DetailedQuote): DetailedQuote {
    // TODO: MERGE IT WITH BASE CLASS METHOD
    if (this.customizedChartEnabled !== undefined && quote?.defaultTimeSeries !== undefined && this.customizedChartEnabled !== null || this.customizedChartEnabled === true) {
      quote.defaultTimeSeries.closeSeries = [];
    }

    if (this.selectedTab() === undefined && quote !== undefined) {
      this.selectedTab.set(this.getInitialTab(quote.issueType));
    }
    return super.requestQuoteSuccess(quote);
  }

  /**
   * Update chart config
   * Called on rest service success
   */
  protected configureChart(quote: DetailedQuote): void {
    if (!this.customizedChartEnabled) {
      super.configureChart(quote);
      return;
    }

    let selectTimeRange;
    const selectChartSettings = this.quoteChartConfigService.getChartSettings(quote.issueType);

    this.template = QuoteTemplate.ISSUE_TYPE;
    this.displayTimeSeries.tabs = quote.issueType === QuoteIssueType.MF ?
      QuoteConstants.TABS_MF : QuoteConstants.TABS_DEFAULT;

    this.chartConfig = this.quoteChartConfigService.createTimeSeriesStockChartConfig(quote);

    this.displayTimeSeries.timeRanges = quote.issueType === QuoteIssueType.MF ?
      QuoteConstants.DEFAULT_TIME_RANGE_SELECTOR.mutualFund : QuoteConstants.DEFAULT_TIME_RANGE_SELECTOR.others;

    if (selectChartSettings !== undefined && selectChartSettings.selectType !== undefined) {
      this.displayTimeSeries.selectType = QuoteConstants.CHART_TYPE_SELECTOR
        .find((chartTypeSelector: ChartTypeSelector): boolean => chartTypeSelector.type === selectChartSettings.selectType.type);
    } else {
      this.displayTimeSeries.selectType = QuoteConstants.CHART_TYPE_SELECTOR[0];
    }

    if (selectChartSettings !== undefined) {
      selectTimeRange = this.displayTimeSeries.timeRanges
        .find( (timeRange: TimeRangeSelector): boolean =>  timeRange.type === selectChartSettings.selectTimeRange.type);
    }

    if (selectTimeRange === undefined ) {
      this.displayTimeSeries.selectTimeRange = this.displayTimeSeries.timeRanges[0];
      this.displayTimeSeries.selectFrequency = QuoteConstants.DEFAULT_FREQUENCY_SELECTOR.DAILY[0];

      this.assignmentFrequencies(this.displayTimeSeries.selectTimeRange);
    } else {
      this.displayTimeSeries.selectTimeRange = selectTimeRange;
      this.assignmentFrequencies(this.displayTimeSeries.selectTimeRange, selectChartSettings.selectFrequency);
    }
    this.quoteChartConfigService.addChartVolume(this.chartConfig);
    this.displayTimeSeriesEnableFrequency(this.displayTimeSeries.selectTimeRange);
  }

  /**
   * Gets initial tab from local storage
   */
  private getInitialTab(type: QuoteIssueType): DetailedQuoteTab {
    const storedObject = this.localStorageService.getItem(QuoteConstants.LOCAL_STORAGE_KEY);
    const consumerKey = this.hideAddToWatchlistButton() !== undefined ?
      QuoteConstants.WATCHLIST : QuoteConstants.HOLDINGS;
    const initialTabByConsumer = consumerKey === QuoteConstants.HOLDINGS ?
      QuoteConstants.SUMMARY_TAB : QuoteConstants.MORE_DETAILS_TAB;
    type = this.getLocalStorageQuoteType(type);
    if (storedObject !== null && storedObject[type] !== undefined) {
      return storedObject[type];
    } else {
      return initialTabByConsumer as DetailedQuoteTab;
    }
  }

  /**
   * get local storage type based on issue type
   */
  private getLocalStorageQuoteType(type: QuoteIssueType): QuoteIssueType {
    return type === QuoteIssueType.ETF ? QuoteIssueType.STOCK : type;
  }

  /**
   * Save chart settings
   *
   */
  private saveChartSettings(): void {
    const rememberSettings: QuoteChartSettings = {
      selectTimeRange: this.displayTimeSeries.selectTimeRange,
      selectFrequency: this.displayTimeSeries.selectFrequency,
      selectType: this.displayTimeSeries.selectType
    };
    this.quoteChartConfigService.setChartSettings(rememberSettings, this.quote().issueType);
  }

  /**
   * Assignment frequencies
   */
  private assignmentFrequencies(timeRange: TimeRangeSelector, frequency: FrequencySelector = undefined): void {
    if (timeRange.frequencies === undefined) {
      return;
    }

    this.displayTimeSeries.frequencies = QuoteConstants.DEFAULT_FREQUENCY_SELECTOR[timeRange.frequencies];
    if (frequency !== undefined) {
      this.displayTimeSeries.selectFrequency = frequency;
    }

    this.displayTimeSeries.selectFrequency = this.displayTimeSeries.frequencies
      .find((freq: FrequencySelector): boolean =>  freq.timePeriod === this.displayTimeSeries.selectFrequency.timePeriod &&
        freq.interval === this.displayTimeSeries.selectFrequency.interval);

    if (this.displayTimeSeries.selectFrequency === undefined) {
      if (timeRange.chartSeries === QuoteTimeRange.THREE_YEARS || timeRange.chartSeries === QuoteTimeRange.FIVE_YEARS ||
          timeRange.chartSeries === QuoteTimeRange.TEN_YEARS || timeRange.chartSeries === QuoteTimeRange.TWENTY_YEARS ) {
        this.displayTimeSeries.selectFrequency = this.displayTimeSeries.frequencies[1];
      } else {
        this.displayTimeSeries.selectFrequency = this.displayTimeSeries.frequencies[0];
      }
    }
  }

  /**
   * Enable frequency for chart
   */
  private displayTimeSeriesEnableFrequency(timeRange: TimeRangeSelector): void {
    if (timeRange.chartSeries !== undefined) {
      const request: QuoteFrequencyParams = {
        symbol: this.quote().tickerSymbolForTimeSeries,
        market: this.getSymbolMarket(),
        timeRange: timeRange.type,
        timePeriod: this.displayTimeSeries.selectFrequency.timePeriod,
        interval: this.displayTimeSeries.selectFrequency.interval
      };

      const updateChart = this.dailyTimeSeriesFrequency(timeRange.chartSeries);
      if (updateChart !== undefined) {
        this.getTimeSeriesEnabledFrequency(request, updateChart);
      } else {
        if (timeRange.chartSeries === QuoteTimeRange.INTRADAY) {
          this.displayIntraDayTimeSeriesEnableFrequency(request);
        } else {
          this.displayDefaultTimeSeries();
        }
      }
    }
  }

  /**
   * Gets time series data
   */
  private getTimeSeriesEnabledFrequency(request: QuoteFrequencyParams, updateChartSeries: unknown): void {
    this.change.next();
    this.quoteRestService.getTimeSeriesEnableFrequency(request)
      .pipe(takeUntil(this.change), takeUntilDestroyed(this.destroyRef))
      .subscribe((timeSeries: QuoteTimeSeries): void => {
        this.requestTimeSeriesSuccess(timeSeries, updateChartSeries);
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * Gets intra daytime series data
   */
  private displayIntraDayTimeSeriesEnableFrequency(request: QuoteFrequencyParams): void {
    this.change.next();
    this.quoteRestService.getTimeSeriesEnableFrequency(request)
      .pipe(takeUntil(this.change), takeUntilDestroyed(this.destroyRef))
      .subscribe((timeSeries: QuoteTimeSeries): void => {
        this.requestTimeSeriesSuccess(timeSeries, undefined);
        this.changeDetectorRef.markForCheck();
      });
  }
}
